<app-vault-module-shell
  moduleLabel="Custodia Legal"
  [accessDenied]="store.accessDenied()"
  [loadError]="store.loadError()"
  [loadErrorTitle]="store.loadErrorTitle() ?? 'No se pudo cargar Custodia Legal'"
>
<section class="legal-hold">
  <app-page-header
    title="Custodia Legal"
    subtitle="Gestiona casos de Legal Hold y asignación de documentos."
    icon="Shield"
    [actions]="headerActions()"
    (actionClick)="onHeaderAction($event)"
  />

  @if (error()) {
    <app-vault-page-alert
      type="error"
      title="Error"
      [message]="error()"
      (closed)="store.clearPageError()"
    />
  }
  @if (info()) {
    <app-vault-page-alert
      type="info"
      title="Aviso"
      [message]="info()"
      (closed)="store.clearPageInfo()"
    />
  }

  @if (creating()) {
    <fieldset #editPanel class="legal-hold__panel">
      <legend>{{ editingId() ? 'Editar caso' : 'Nuevo caso' }}</legend>
      <app-grid-form preset="responsive" wrapperClass="legal-hold__form">
        <app-grid-form-item [span]="2" class="legal-hold-field">
          <app-field-label
            label="Nombre del caso"
            [required]="true"
            tooltip="Nombre visible del caso de custodia legal."
          />
          <lib-input
            label=""
            [externalLabel]="true"
            placeholder="Ej: Proceso laboral 2026"
            [value]="form().case_name"
            (valueChange)="onFormField('case_name', $event)"
          />
          <app-vault-form-validation-alert [message]="store.formNameError()" />
        </app-grid-form-item>
        <app-grid-form-item [span]="2" class="legal-hold-field">
          <app-field-label
            label="Número de caso"
            tooltip="Código o radicado del expediente asociado al caso."
          />
          <lib-input
            label=""
            placeholder="Ej: EXP-2026-01"
            [value]="form().case_number"
            (valueChange)="onFormField('case_number', $event)"
          />
        </app-grid-form-item>
        <app-grid-form-item [span]="2" class="legal-hold-field">
          <app-field-label
            label="Descripción"
            tooltip="Resumen breve del objetivo y alcance del caso."
          />
          <lib-input
            label=""
            placeholder="Detalle breve del caso"
            [value]="form().description"
            (valueChange)="onFormField('description', $event)"
          />
        </app-grid-form-item>
        <app-grid-form-item [span]="2" class="legal-hold-field">
          <app-field-label
            label="Fecha fin (opcional)"
            tooltip="Fecha estimada de cierre del caso."
          />
          <lib-datepicker
            label=""
            [value]="form().end_date"
            (valueChange)="onFormField('end_date', $event)"
          />
        </app-grid-form-item>
      </app-grid-form>
      <div class="legal-hold__actions">
        <lib-button variant="primary" [loading]="saving()" (clicked)="saveCase()">Guardar</lib-button>
        <lib-button variant="secondary" (clicked)="closeCaseForm()">Cancelar</lib-button>
      </div>
    </fieldset>
  }

  <app-grid-form preset="responsive" wrapperClass="legal-hold__filters">
    <app-grid-form-item [span]="2" class="legal-hold-field">
      <app-field-label
        label="Buscar caso"
        tooltip="Busca por nombre del caso o por número de expediente."
      />
      <lib-search
        placeholder="Buscar por nombre o número..."
        [searchTerm]="searchTerm()"
        (searchTermChange)="onSearchTermChange($event)"
        [loading]="loading()"
      />
    </app-grid-form-item>
    <app-grid-form-item [span]="2" class="legal-hold-field legal-hold-field--status">
      <app-field-label
        label="Estado"
        tooltip="Filtra los casos por estado activo o cerrado."
      />
      <lib-select
        label=""
        [value]="statusFilter()"
        [options]="statusOptions"
        (valueChange)="onStatusFilterChange($event)"
      />
    </app-grid-form-item>
  </app-grid-form>

  @if (filteredRows().length > 0) {
    <lib-table
      [config]="tableConfig()"
      [data]="filteredRows()"
      [loading]="loading()"
      [pageSize]="tablePageSize()"
      [pageSizeOptions]="tablePageSizeOptions"
      (pageChange)="onTablePageChange($event)"
      (action)="onTableAction($event)"
    >
      <ng-template #cellTemplate let-row let-column="column">
        @if (column.key === 'statusLabel') {
          <lib-badge [text]="row.statusLabel" [variant]="row.status === 'active' ? 'success' : 'neutral'" size="small" />
        }
        @if (column.key === '_actions') {
          <div class="legal-hold__row-actions" (click)="$event.stopPropagation()">
            <lib-button
              variant="icon-ghost"
              type="button"
              prefixIcon="Eye"
              [iconSize]="20"
              ariaLabel="Ver caso"
              (clicked)="onRowAction('view', row)"
            />
            <lib-button
              variant="icon-ghost"
              type="button"
              prefixIcon="Pencil"
              [iconSize]="20"
              ariaLabel="Editar caso"
              (clicked)="onRowAction('edit', row)"
            />
            @if (row.status === 'active') {
              <lib-button
                variant="icon-ghost"
                type="button"
                prefixIcon="LockOpen"
                [iconSize]="20"
                ariaLabel="Cerrar caso"
                (clicked)="onRowAction('close', row)"
              />
            } @else {
              <lib-button
                variant="icon-ghost"
                type="button"
                prefixIcon="Lock"
                [iconSize]="20"
                ariaLabel="Reabrir caso"
                (clicked)="onRowAction('reopen', row)"
              />
            }
            <lib-button
              variant="icon-ghost"
              type="button"
              prefixIcon="Trash2"
              [iconSize]="20"
              ariaLabel="Eliminar caso"
              (clicked)="onRowAction('delete', row)"
            />
          </div>
        }
      </ng-template>
    </lib-table>
  } @else if (loading()) {
    <div class="loading-container">
      <lib-empty message="Cargando casos" description="Espere por favor..." />
    </div>
  } @else {
    <lib-empty icon="Shield" message="Sin casos de legal hold" description="Crea un caso para iniciar la custodia legal." />
  }

  @if (selected(); as selectedCase) {
    <section class="legal-hold__detail">
      <div class="legal-hold__detail-head">
        <h3>{{ selectedCase.case_name }}</h3>
        <div class="legal-hold__actions">
          @if (selectedCase.status === 'active') {
            <lib-button variant="secondary" (clicked)="openAssignmentDialog()">Asignar documento</lib-button>
          }
        </div>
      </div>
      <p class="legal-hold__hint">Historial de documentos en custodia</p>
      @if (selectedDocuments().length > 0) {
        <div class="legal-hold__documents">
          @for (doc of selectedDocuments(); track doc.id) {
            <article class="legal-hold__doc">
              <div>
                <strong>{{ doc.document_name || doc.document_id }}</strong>
                <p>{{ doc.reason || 'Sin motivo registrado' }}</p>
              </div>
              @if (selectedCase.status === 'active') {
                <lib-button variant="secondary" (clicked)="removeDocument(doc)">Remover</lib-button>
              }
            </article>
          }
        </div>
      } @else {
        <lib-empty icon="FileX" message="Sin documentos asignados" />
      }
    </section>
  }

  @if (assignmentDialogOpen()) {
    <div class="legal-hold__backdrop" (click)="closeAssignmentDialog()"></div>
    <div class="legal-hold__dialog">
      <h3>Asignar documento al caso</h3>
      <lib-select
        label="Tipo documento"
        [value]="assignmentType()"
        [options]="assignmentTypeOptions"
        (valueChange)="onAssignmentTypeChange($event)"
      />
      <lib-search
        placeholder="Buscar documento..."
        [searchTerm]="assignmentSearch()"
        (searchTermChange)="onAssignmentSearchChange($event)"
      />
      <div class="legal-hold__options">
        @for (option of availableDocuments(); track option.id) {
          <lib-button
            variant="secondary"
            class="legal-hold__option"
            [class.legal-hold__option--selected]="selectedDocumentId() === option.id"
            (clicked)="selectDocumentOption(option.id)"
          >
            <span>{{ option.name }}</span>
            @if (option.inHold) {
              <lib-badge text="Ya en hold" variant="warning" size="small" />
            }
          </lib-button>
        }
      </div>
      <lib-input
        label="Motivo (opcional)"
        [value]="assignmentReason()"
        (valueChange)="onAssignmentReasonChange($event)"
      />
      <div class="legal-hold__actions">
        <lib-button variant="secondary" (clicked)="closeAssignmentDialog()">Cancelar</lib-button>
        <lib-button variant="primary" [loading]="saving()" (clicked)="assignDocument()">Asignar</lib-button>
      </div>
    </div>
  }
</section>

</app-vault-module-shell>
